import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCheck,
  faEnvelope,
  faPhone,
  faShieldHalved,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import emailjs from "emailjs-com";
import image from "../../images/AxiomosLogo.png";
import "./Talk.css";

const EMAILJS_CONFIG = {
  serviceId:
    process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_ystlmqj",
  templateId:
    process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_pwxep1n",
  publicKey:
    process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "efnQ_-lOOJQ0YcFtY",
};

const initialStatus = { type: "idle", message: "" };

function Talk({ id = "contact" }) {
  const [status, setStatus] = useState(initialStatus);
  const isSending = status.type === "sending";

  const sendEmail = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    setStatus({
      type: "sending",
      message: "Sending your project details…",
    });

    try {
      await emailjs.sendForm(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        form,
        EMAILJS_CONFIG.publicKey,
      );

      form.reset();
      setStatus({
        type: "success",
        message: "Thanks — your message is on its way. We’ll respond within one business day.",
      });
    } catch (error) {
      console.error("EmailJS submission failed:", error);
      setStatus({
        type: "error",
        message: "We couldn’t send your message. Please try again or contact us directly.",
      });
    }
  };

  const clearPreviousStatus = () => {
    if (status.type === "success" || status.type === "error") {
      setStatus(initialStatus);
    }
  };

  return (
    <section id={id} className="talk-section" aria-labelledby="talk-title">
      <div className="talk-section__container">
        <div className="talk-section__card">
          <div className="talk-section__form-panel">
            <p className="talk-section__eyebrow">Start a conversation</p>
            <h2 id="talk-title" className="talk-section__title">
              Let’s turn your next idea into something useful.
            </h2>
            <p className="talk-section__description">
              Tell us about your goals, challenges, or early-stage idea. Our team
              will review the details and reply with practical next steps within
              one business day.
            </p>

            <form
              className="talk-form"
              onSubmit={sendEmail}
              onChange={clearPreviousStatus}
            >
              <div className="talk-form__fields">
                <div className="talk-form__group">
                  <label htmlFor="talk-name">Your name</label>
                  <input
                    type="text"
                    id="talk-name"
                    name="name"
                    placeholder="John Smith"
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="talk-form__group">
                  <label htmlFor="talk-email">Work email</label>
                  <input
                    type="email"
                    id="talk-email"
                    name="email"
                    placeholder="john@company.com"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className="talk-form__group">
                <label htmlFor="talk-about">Tell us about your project</label>
                <textarea
                  id="talk-about"
                  name="about"
                  rows="5"
                  placeholder="What would you like to build or improve?"
                  required
                />
              </div>

              <label className="talk-form__consent" htmlFor="talk-privacy">
                <input type="checkbox" id="talk-privacy" required />
                <span>
                  I agree to Axiomos’s{" "}
                  <Link to="/privacy-policy">Privacy Policy</Link>.
                </span>
              </label>

              <div className="talk-form__footer">
                <button
                  type="submit"
                  className="talk-form__submit"
                  disabled={isSending}
                  aria-busy={isSending}
                >
                  {isSending ? (
                    <>
                      Sending
                      <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
                    </>
                  ) : (
                    <>
                      Start your project
                      <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                    </>
                  )}
                </button>

                <p className="talk-form__response-time">
                  <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                  Response within one business day
                </p>
              </div>

              {status.message && (
                <p
                  className={`talk-form__status talk-form__status--${status.type}`}
                  role={status.type === "error" ? "alert" : "status"}
                  aria-live="polite"
                >
                  {status.message}
                </p>
              )}
            </form>
          </div>

          <aside className="talk-section__contact-panel" aria-label="Direct contact options">
            <div className="talk-contact__brand">
              <img src={image} alt="Axiomos Technologies" />
            </div>

            <div className="talk-contact__profile">
              <p className="talk-contact__label">Your point of contact</p>
              <h3>Shiwanshu Pandey</h3>
              <p>Head of Partner Engagement</p>
            </div>

            <div className="talk-contact__methods">
              <a href="mailto:axiomos2024@gmail.com" className="talk-contact__method">
                <span className="talk-contact__method-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
                <span>
                  <small>Email us</small>
                  axiomos2024@gmail.com
                </span>
              </a>

              <a href="tel:+917738899255" className="talk-contact__method">
                <span className="talk-contact__method-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={faPhone} />
                </span>
                <span>
                  <small>Call us</small>
                  +91 77388 99255
                </span>
              </a>
            </div>

            <a
              href="https://wa.me/917738899255?text=Hi%20Shiwanshu%2C%20I%27d%20like%20to%20discuss%20a%20project."
              className="talk-contact__whatsapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faWhatsapp} aria-hidden="true" />
              Continue on WhatsApp
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </a>

            <p className="talk-contact__privacy">
              <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
              Your project details are treated as confidential.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default Talk;
